/**
 * Mobile Drawer Navigation Hook
 *
 * Handles navigation state and breadcrumb management for the mobile drawer.
 * Manages navigation stack, submenu navigation, and drawer state synchronization.
 */

import { useState, useEffect, useCallback } from "react";
import { NavigationItem } from "../types";
import { BreadcrumbItem } from "../mobile-drawer-types";

interface UseMobileDrawerNavigationProps {
  items: NavigationItem[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function useMobileDrawerNavigation({
  items,
  isOpen,
  onOpenChange,
}: UseMobileDrawerNavigationProps) {
  const [navigationStack, setNavigationStack] = useState<BreadcrumbItem[]>([
    { id: "root", name: "Menu", items },
  ]);

  const currentLevel = navigationStack[navigationStack.length - 1];

  // Navigate into a submenu
  const navigateToSubmenu = useCallback((item: NavigationItem) => {
    if (item.children && item.children.length > 0) {
      setNavigationStack((prev) => [
        ...prev,
        { id: item.id, name: item.name, items: item.children! },
      ]);
    }
  }, []);

  // Navigate back to previous level
  const navigateBack = useCallback(() => {
    if (navigationStack.length > 1) {
      setNavigationStack((prev) => prev.slice(0, -1));
    }
  }, [navigationStack.length]);

  // Navigate to root level
  const navigateToRoot = useCallback(() => {
    setNavigationStack([{ id: "root", name: "Menu", items }]);
  }, [items]);

  // Navigate to a specific level in the stack
  const navigateToLevel = useCallback(
    (levelIndex: number) => {
      if (levelIndex >= 0 && levelIndex < navigationStack.length) {
        setNavigationStack((prev) => prev.slice(0, levelIndex + 1));
      }
    },
    [navigationStack.length]
  );

  // Handle item click (navigate to submenu or close drawer)
  const handleItemClick = useCallback(
    (item: NavigationItem) => {
      if (item.children && item.children.length > 0) {
        navigateToSubmenu(item);
      } else {
        // Close drawer when navigating to a page
        onOpenChange(false);
      }
    },
    [navigateToSubmenu, onOpenChange]
  );

  // Reset navigation stack when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setNavigationStack([{ id: "root", name: "Menu", items }]);
    }
  }, [isOpen, items]);

  return {
    navigationStack,
    currentLevel,
    navigateToSubmenu,
    navigateBack,
    navigateToRoot,
    navigateToLevel,
    handleItemClick,
    setNavigationStack,
  };
}
